import React from 'react';
import { FlattenedListUploadEntry, ListUploadEntry } from './FileUploadDetails';
import { FileConversionStatus, FileScanStatus, SftJobStatus, UploadStatus } from '../../types/FileStatuses';
import {
    ReloadOutlined,
    CloudUploadOutlined,
    ExclamationCircleOutlined,
    MonitorOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { DownloadAttachmentButton } from './DownloadAttachmentButton';

import settings from '../../settings.json';
const settingEnv = (settings as any)[window.location.host] || settings.default;
const sftEnabled = settingEnv.sft_enabled;

type UploadStatusColumnProps = {
    /** Describes uploads for which files have not yet been uploaded */
    record: Omit<ListUploadEntry, 'files'>;
    requestLoading: boolean;
    resendRequest: (caseId: string, uploadId: string) => Promise<void>;
}

const AwaitingUploadButton = (
    <Button
        type='default'
        disabled
        icon={<CloudUploadOutlined />}
        style={{
            backgroundColor: '#fff',
            color: '#1998d5',
            border: '1px solid #1998d5',
            margin: '-5px 0px',
        }}

    >
        Awaiting Upload
    </Button>
);

/** When an upload does not yet have files, the upload status is used to render relevant information */
export function UploadStatusColumn ({
    record,
    requestLoading,
    resendRequest,
}: UploadStatusColumnProps) {
    switch(record.status) {
        case UploadStatus.Requested:
            return (
                <Button
                    type='default'
                    onClick={() => {
                        resendRequest(record.caseId, record.uploadId);
                    }}
                    loading={requestLoading}
                    icon={<ReloadOutlined />}
                    style={{
                        color: 'orange',
                        border: '1px solid orange',
                        margin: '5px 0px',
                    }}
                >
                    Resend Request
                </Button>
            );
        case UploadStatus.Uploading:
            return AwaitingUploadButton;
        case UploadStatus.Expired:
        default: // Uploads should not be in any other state without having files
            return (
                <Button
                    type='default'
                    disabled
                    icon={<ExclamationCircleOutlined />}
                    style={{
                        backgroundColor: '#fff',
                        color: 'red',
                        border: '1px solid red',
                        margin: '-5px 0px',
                    }}

                >
                    Expired
                </Button>
            );
    }
}


const ScanningFileButton = (
    <Button
        type='default'
        disabled
        icon={<MonitorOutlined />}
        style={{
            backgroundColor: '#fff',
            color: '#1998d5',
            border: '1px solid #1998d5',
            margin: '-5px 0px',
        }}

    >
        Scanning File
    </Button>
);

const ConvertingFileButton = (
    <Button
        type='default'
        disabled
        icon={<MonitorOutlined />}
        style={{
            backgroundColor: '#fff',
            color: '#1998d5',
            border: '1px solid #1998d5',
            margin: '-5px 0px',
        }}

    >
        Converting File
    </Button>
);

const InvalidFileButton = (
    <Button
        type='default'
        disabled
        icon={<ExclamationCircleOutlined />}
        style={{
            backgroundColor: '#fff',
            color: 'red',
            border: '1px solid red',
            margin: '-5px 0px',
        }}

    >
        Invalid File
    </Button>
);

const FileUnavailableButton = (
    <Button
        type='default'
        disabled
        icon={<WarningOutlined />}
        style={{
            backgroundColor: '#fff',
            color: 'orange',
            border: '1px solid orange',
            margin: '-5px 0px',
        }}

    >
        File Unavailable
    </Button>
)

type FileStatusColumnProps = {
    record: FlattenedListUploadEntry;
};

function LegacyFileStatusColumn ({
    record,
}: FileStatusColumnProps) {
    const {
        scanStatus,
        conversionStatus = FileConversionStatus.NA,
    } = record;

    const scanComplete = scanStatus !== FileScanStatus.NotStarted && scanStatus !== FileScanStatus.Pending;
    const noMalware = scanStatus === FileScanStatus.NoMalware;

    const conversionSuccess = conversionStatus === FileConversionStatus.Completed;
    const conversionFailure = conversionStatus === FileConversionStatus.Failed;
    const conversionComplete = conversionSuccess || conversionFailure;

    if (scanStatus === FileScanStatus.NotStarted) {
        return AwaitingUploadButton;
    }

    if (scanStatus === FileScanStatus.Pending) {
        return ScanningFileButton;
    }

    if (noMalware && !conversionComplete) {
        return ConvertingFileButton;
    }

    if ((scanComplete && !noMalware) || conversionFailure) {
        return InvalidFileButton;
    }

    if (noMalware && conversionSuccess) {
        return (
            <DownloadAttachmentButton
                caseId={record.caseId}
                uploadId={record.uploadId}
                fileName={record.name!}
                fileId={record.fileId}
                multiFile={!!record.fileId}
            />
        );
    }

    // Default value. ScanStatus and ConversionStatus values are such that this line will not be reached
    return <></>;
}

export function FileStatusColumn({
    record,
}: FileStatusColumnProps) {
    if (!sftEnabled || !record.sftJobStatus) {
        return <LegacyFileStatusColumn record={record} />
    }
    const { sftJobStatus, availableForDownload } = record;

    switch(sftJobStatus) {
        case SftJobStatus.NOT_STARTED:
            return AwaitingUploadButton;
        case SftJobStatus.PENDING:
            return ScanningFileButton;
        case SftJobStatus.COMPLETED:
            if (!availableForDownload) return FileUnavailableButton;
            return (
                <DownloadAttachmentButton
                    caseId={record.caseId}
                    uploadId={record.uploadId}
                    fileName={record.name!}
                    fileId={record.fileId}
                    multiFile={!!record.fileId}
                />
            );
        case SftJobStatus.MALWARE:
        case SftJobStatus.FAILED:
        default:
            return InvalidFileButton;
    }
}
